import type { ReactNode, ButtonHTMLAttributes } from "react";

// Utility to merge class names
function cn(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

interface PrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  className?: string;
}

/**
 * Primary button with hover state
 * Matches shadcn Button default variant
 */
export function PrimaryButton({
  children,
  disabled,
  className,
  ...props
}: PrimaryButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled}
      className={cn(
        // Layout
        "inline-flex items-center justify-center",
        // Sizing & spacing
        "px-6 py-3",
        // Typography
        "text-base font-medium",
        // Colors
        "bg-primary text-primary-foreground",
        // Border
        "border-none rounded-lg",
        // Hover (only when not disabled)
        "hover:opacity-90",
        // Disabled state
        "disabled:opacity-50 disabled:cursor-not-allowed",
        // Transitions
        "transition-opacity duration-150",
        // Custom classes
        className,
      )}
    >
      {children}
    </button>
  );
}

interface OutlineButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  className?: string;
}

/**
 * Outline button with hover state
 * Matches shadcn Button outline variant
 */
export function OutlineButton({
  children,
  disabled,
  className,
  ...props
}: OutlineButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled}
      className={cn(
        // Layout
        "inline-flex items-center justify-center",
        // Sizing & spacing
        "px-6 py-3",
        // Typography
        "text-base font-medium",
        // Colors
        "bg-background text-foreground",
        // Border
        "border border-border rounded-lg",
        // Hover
        "hover:bg-accent",
        // Disabled state
        "disabled:opacity-50 disabled:cursor-not-allowed",
        // Transitions
        "transition-colors duration-150",
        // Custom classes
        className,
      )}
    >
      {children}
    </button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const iconButtonSizes = {
  sm: "w-10 h-10",
  md: "w-12 h-12",
  lg: "w-14 h-14",
};

/**
 * Icon button (circular) with hover state
 * Matches shadcn Button variant="outline" size="icon"
 */
export function IconButton({
  children,
  disabled,
  size = "md",
  className,
  ...props
}: IconButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled}
      className={cn(
        // Layout
        "inline-flex items-center justify-center",
        // Size
        iconButtonSizes[size],
        // Typography
        "text-sm font-medium",
        // Colors
        "bg-card text-foreground",
        // Border
        "border border-border rounded-full",
        // Shadow
        "shadow-sm",
        // Hover
        "hover:bg-accent",
        // Disabled state
        "disabled:opacity-50 disabled:cursor-not-allowed",
        // Transitions
        "transition-colors duration-150",
        // Custom classes
        className,
      )}
    >
      {children}
    </button>
  );
}
